import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { healthReadinessQueryKey } from './health-readiness';

/**
 * Health-check findings register surface (T-0071 / E07-F07).
 *
 * Engagement-scoped, mounted under
 * `/api/v1/engagements/:engagement_id/health/findings`. The list endpoint
 * is cursor-paginated and supports filters by category, severity, status,
 * source_kind, and health_domain_id. The T-0105 findings screen reads
 * the list to render a consolidated, filterable register; it does not
 * mutate (create / edit / confirm-from-proposal lives behind the
 * generic create modal — T-0117 — and proposal review at T-0099).
 *
 * Response shape matches `services/health-findings.ts → FindingListItemDto`:
 *   - id, payload (JSON), status, severity, category, health_domain_id,
 *     origin, inconsistency_flag
 *
 * Origin enum is the backend RegisterEntry origin (`ai_extracted_*` /
 * `manually_authored`); the UI maps to the `<ProvenanceChip>` vocabulary
 * via `backendOriginToProvenance` in lib/api/registers.ts.
 *
 * T-0240 adds `created_at` (the finding's age) and `resolved_at` to the
 * list rows. T-0244 adds the edit: `PATCH …/health/findings/:id` resolves,
 * reopens or waives a finding (status) and edits its title, type,
 * severity and domain; a severity change carries the override reason the
 * API requires.
 */

export const FindingListItemSchema = z.object({
  id: z.string().uuid(),
  payload: z.unknown(),
  status: z.string(),
  severity: z.string().nullable(),
  category: z.string().nullable(),
  health_domain_id: z.string().uuid().nullable(),
  origin: z.string(),
  inconsistency_flag: z.boolean(),
  /** T-0240: when the finding was raised (optional from an older API). */
  created_at: z.string().optional(),
  /** T-0240: when it moved to a closed status; null while open. */
  resolved_at: z.string().nullable().optional(),
});
export type FindingListItem = z.infer<typeof FindingListItemSchema>;

export const FindingListSchema = z.object({
  items: z.array(FindingListItemSchema),
  next_cursor: z.string().nullable(),
});
export type FindingList = z.infer<typeof FindingListSchema>;

export interface ListFindingsOpts {
  category?: string;
  severity?: string;
  status?: string;
  source_kind?: string;
  health_domain_id?: string;
  cursor?: string;
  limit?: number;
}

export function listFindings(
  engagementId: string,
  opts: ListFindingsOpts = {},
): Promise<FindingList> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/health/findings`,
    FindingListSchema,
    { query: opts as Record<string, string | number | boolean | undefined> },
  );
}

export function useFindingListQuery(engagementId: string | undefined, opts: ListFindingsOpts = {}) {
  return useQuery({
    queryKey: ['health-findings', engagementId, opts] as const,
    queryFn: () => listFindings(engagementId!, opts),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}

/**
 * Permissive payload field extractor — `payload` is a JSON column on
 * `register_entries`. Mirrors `extractPayloadString` in the issues route.
 * Returns undefined for absent or non-string fields.
 */
export function extractFindingPayloadString(payload: unknown, key: string): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const v = (payload as Record<string, unknown>)[key];
  if (typeof v === 'string' && v.length > 0) return v;
  return undefined;
}

// ===========================================================================
// Finding create (T-0076 backend — client wiring added in Phase 1)
// ===========================================================================

export interface CreateFindingBody {
  title: string;
  /** One of the API's severities (`GET …/health-readiness/finding-options`). */
  severity: string;
  /** The finding's type; the register keeps it in `category`. */
  category: string;
  /** One of the API's source kinds (`GET …/health-readiness/finding-options`). */
  source_kind: string;
  description?: string | null;
  health_domain_id?: string | null;
  /** Confirming an AI proposal: the API flips it to accepted in the same write. */
  from_proposal_id?: string | null;
  /** With from_proposal_id: the note's document, linked as the finding's evidence. */
  source_document_id?: string | null;
}

const FindingWriteResultSchema = z.object({ id: z.string().optional() }).passthrough();

export function useCreateFindingMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFindingBody) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/health/findings`,
        FindingWriteResultSchema,
        { method: 'POST', body },
      ),
    onSuccess: () => invalidateFindings(qc, engagementId),
  });
}

/**
 * A finding write moves the register and the composite read's counts (the
 * Findings tab's KPIs, the open findings by domain, the Overview card).
 */
function invalidateFindings(qc: ReturnType<typeof useQueryClient>, engagementId?: string) {
  void qc.invalidateQueries({ queryKey: ['health-findings', engagementId] });
  void qc.invalidateQueries({ queryKey: healthReadinessQueryKey(engagementId) });
}

// ===========================================================================
// Finding edit (T-0244): PATCH …/health/findings/:id
// ===========================================================================

/** The statuses that close a finding; moving out of one reopens it (T-0240). */
export const CLOSED_FINDING_STATUSES: ReadonlySet<string> = new Set(['resolved', 'waived']);

export interface UpdateFindingBody {
  /** open / acknowledged / remediating / resolved / waived. */
  status?: string;
  title?: string;
  /** The finding's type; the register keeps it in `category`. */
  category?: string;
  severity?: string;
  health_domain_id?: string | null;
  description?: string | null;
  /** Required by the API whenever the severity changes. */
  override?: { tag: string; rationale: string };
}

const UpdateFindingResultSchema = z
  .object({
    id: z.string(),
    severity: z.string(),
    status: z.string(),
    resolved_at: z.string().nullable().optional(),
  })
  .passthrough();

export function useUpdateFindingMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ findingId, body }: { findingId: string; body: UpdateFindingBody }) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/health/findings/${encodeURIComponent(findingId)}`,
        UpdateFindingResultSchema,
        { method: 'PATCH', body },
      ),
    onSuccess: () => invalidateFindings(qc, engagementId),
  });
}
